import { inject, Injectable, DOCUMENT } from '@angular/core';
import { AudioService } from '../../audio/services/audio/audio.service';
import {
  AudioData,
  AudioDataSequence,
  Population,
  PopulationInlineChoice,
} from 'src/app/shared/interfaces';
import {
  replaceSpecialCharacters,
  generateAudioId,
  splitTextBySentences,
  MAX_TTS_CHARS,
} from './tts-utils';
import { AmiraTTSService, PhonemeProps } from './amira-tts/amira-tts.service';
import { StudentInboxService } from 'src/app/modules/student-inbox-api/services/student-inbox.service';

import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  map,
  merge,
  take,
} from 'rxjs';
import { TemplateID } from 'src/app/shared/enums';
import { WordMetadata } from '../../student-inbox-api/types/student-inbox.types';
import { parseAndGetLastValueAfterToken } from 'src/app/shared/utils/util';
import { ConfigurationService } from '../../configuration/services/configuration/configuration.service';

// Delay in ms before playing prompt to allow page transition animation to finish
export const PROMPT_TRANSITION_DELAY_MS = 1500;

// Interface to store audio data for current population
export interface PopulationAudioData {
  prompt?: AudioData | AudioDataSequence;
  hints?: (AudioData | AudioDataSequence)[];
  feedbackFirstIncorrect?: AudioData | AudioDataSequence;
  feedbackSecondIncorrect?: AudioData | AudioDataSequence;
  answerChoices?: { [key: string]: AudioData | AudioDataSequence };
}

export enum VerbalAudioType {
  UNKNOWN = 'unknown',
  PROMPT = 'prompt',
  HINT = 'hint',
  FEEDBACK_FIRST_INCORRECT = 'feedbackFirstIncorrect',
  FEEDBACK_SECOND_INCORRECT = 'feedbackSecondIncorrect',
}

export enum AudioDataLoadStatus {
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

type PopulationAudioDataMap = {
  [key: string]: {
    status: AudioDataLoadStatus;
    audioData: PopulationAudioData;
  };
};

type WordPartAndPhonemeTokenKeys = {
  tokens: string[] | null;
  displayVal: string;
  actualToken: string;
  parentKey: string;
  wordKey: string;
  displayStr: string;
};

/**
 * This service handles loading and playing audio for the current population.
 */
@Injectable({
  providedIn: 'root',
})
export class VerbalService {
  private currentId = '';
  private currentSequenceId = ''; // Used to track the current sequence being played and to stop it if necessary
  /** Pending `playPopulationAudio(..., true)` timer; must clear on activity switch. */
  private promptPlayDelayTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private document = inject(DOCUMENT);
  currentHintIndex = 0;

  populationToLoad$ = new BehaviorSubject<Population | undefined>(undefined);
  currentPopulationAudioData$ = new BehaviorSubject<
    PopulationAudioData | undefined
  >(undefined);
  private _populationsAudioData: PopulationAudioDataMap = {}; // Map of audio data for all loaded / loading populations

  // Add new Subject for audio completion events
  speechStarted$ = new BehaviorSubject<string>('');
  speechEnded$ = new BehaviorSubject<string>('');
  speechError$ = new BehaviorSubject<{ id: string; error: unknown }>({
    id: '',
    error: new Error(''),
  });

  phonemeStarted$ = new BehaviorSubject<string>('');
  phonemeEnded$ = new BehaviorSubject<string>('');
  phonemeError$ = new BehaviorSubject<{ id: string; error: unknown }>({
    id: '',
    error: new Error(''),
  });

  sequenceStarted$ = new BehaviorSubject<string>('');
  sequenceEnded$ = new BehaviorSubject<string>('');
  sequenceError$ = new BehaviorSubject<{ id: string; error: unknown }>({
    id: '',
    error: new Error(''),
  });

  // Add events for pause/resume state
  audioPaused$ = new BehaviorSubject<string>('');
  audioResumed$ = new BehaviorSubject<string>('');

  constructor(
    private amiraTTSService: AmiraTTSService,
    private audioService: AudioService,
    private studentInboxService: StudentInboxService,
    private configService: ConfigurationService
  ) {}

  // #region Getters and setters

  get populationToLoad(): Population | undefined {
    return this.populationToLoad$.value;
  }

  // Sets the population to load the audioData for and resets the hint index
  set populationToLoad(population: Population) {
    this.populationToLoad$.next(population);
    // Reset hint index when population changes
    this.currentHintIndex = 0;
  }

  get currentPopulationAudioData(): PopulationAudioData | undefined {
    return this.currentPopulationAudioData$.value;
  }

  set currentPopulationAudioData(populationAudioData: PopulationAudioData) {
    this.currentPopulationAudioData$.next(populationAudioData);
  }

  get populationsAudioDataMap(): PopulationAudioDataMap {
    return this._populationsAudioData;
  }

  set currentPopulationId(id: string) {
    this.currentPopulationAudioData = this._populationsAudioData[id]?.audioData;
    this.currentPopulationAudioData$.next(this.currentPopulationAudioData);
  }

  // #region Loading

  /**
   * Replace the phoneme token with the appropriate audio content
   * @param text The text to process
   * @param id The base id for the audio
   * @param index Optional index for the audio
   * @returns Either AudioData for simple text or AudioSequence for text with phonemes
   */

  // eslint-disable-next-line complexity
  private async replacePhonemeToken(
    text: string,
    id: string,
    index?: number
  ): Promise<AudioData | AudioDataSequence> {
    // Check if text contains phoneme token

    const { tokens: tokensArr = [] } = this.parseWordpartAndPhonemeTokens(text);

    if (text.includes('{TARGET_TEXT_PHO}') || tokensArr.length) {
      // If there's no target text and if tokensArr is empty, just replace the phoneme token with empty string
      if (!tokensArr.length && !this.populationToLoad?.targetText) {
        console.warn(
          'VerbalService: No target text found, skipping phoneme processing'
        );
        const processedText = text.replace(/{TARGET_TEXT_PHO}/g, '');
        const audioData: AudioData = {
          id: generateAudioId({ id, text: processedText }, index),
          text: processedText,
        };
        return audioData;
      }

      if (text.includes('{TARGET_TEXT_PHO}')) {
        tokensArr.push('{TARGET_TEXT_PHO}');
      }

      try {
        const phonemeAudioData: { [key: string]: AudioData[] } = {}; // Map to store audio data for each phoneme / wordpart / TARGET_TEXT_PHO token

        // Process each token to get phoneme audio data and store it as a map
        for (const token of tokensArr) {
          const parsedToken =
            token === '{TARGET_TEXT_PHO}'
              ? (this.populationToLoad?.targetText as string)
              : token;
          const {
            tokens: currToken,
            parentKey,
            wordKey,
          } = this.parseIndividualWordPartOrPhonemeToken(parsedToken);
          // check if worpart / pho token exists in the target text
          if (currToken && currToken[0]) {
            if (parentKey === 'SOUND') {
              // If the token is a sound, replace it with the wordKey
              text = text.replace(token, wordKey);
              continue; // Skip further processing for SOUND tokens
            }
            // If token exists, get phoneme audio data based on the parentKey in local store
            const phonemeProps =
              parentKey === 'WORDPART'
                ? this.amiraTTSService.getPhonemeSoundsForWordParts([
                    wordKey.trim(),
                  ])
                : this.amiraTTSService.getPhonemeSounds(wordKey.trim());
            phonemeAudioData[token] = phonemeProps.map(props => ({
              id: `phoneme-${props.friendlyName}`,
              text: props.phoneme,
              url: props.url,
              containsPhonemes: true,
              triggerCorrectiveFeedback: token === '{TARGET_TEXT_PHO}', // Only trigger phoneme playback for TARGET_TEXT_PHO
            }));
          }
          // Get word metadata and phonemes
          else {
            let wordMetadata = { phonemes: [] as string[] } as WordMetadata;
            if (this.populationToLoad?.targetText) {
              wordMetadata = await this.studentInboxService.getWordMetadata(
                this.populationToLoad?.targetText || ''
              );
            }
            const phonemeProps = this.amiraTTSService.getPhonemeSounds(
              wordMetadata?.phonemes ?? []
            );

            phonemeAudioData[token] = phonemeProps.map(props => ({
              id: `phoneme-${props.friendlyName}`,
              text: props.phoneme,
              url: props.url,
              containsPhonemes: true,
              triggerCorrectiveFeedback: token === '{TARGET_TEXT_PHO}', // Only trigger phoneme playback for TARGET_TEXT_PHO
            }));
          }
        }
        // If no phoneme audio data found, return simple AudioData
        if (!Object.keys(phonemeAudioData).length) {
          const audioData: AudioData = {
            id: generateAudioId({ id, text: text }, index),
            text: text,
            containsPhonemes: false,
          };
          return audioData;
        }

        // Split text by the phoneme token while keeping the delimiter
        const delimiters = tokensArr.map(token =>
          token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        // Split the text by the pipe
        const delimiterRegex = new RegExp(`(${delimiters.join('|')})`, 'g');
        const parts = text.split(delimiterRegex).filter(Boolean);

        // Create sequence of audio parts
        const sequence: AudioDataSequence = {
          id: generateAudioId({ id, text: text }, index),
          parts: [],
          type: 'sequence',
          onStart: () => {
            this.sequenceStarted$.next(id);
          },
          onEnd: () => {
            this.sequenceEnded$.next(id);
          },
          onError: (error: Error) => {
            console.error('Error in audio sequence:', error);
            this.sequenceError$.next({ id, error });
          },
        };

        // Process all parts
        parts.forEach(part => {
          if (tokensArr?.includes(part)) {
            // Add phonemes when we encounter the token
            if (phonemeAudioData[part] && phonemeAudioData[part].length) {
              sequence.parts.push(...phonemeAudioData[part]);
              sequence.containsPhonemes = true;
              if (part === '{TARGET_TEXT_PHO}')
                sequence.triggerCorrectiveFeedback = true;
            }
          } else {
            // check if text has alphanumeric value in it before adding it to sequence
            const regex = /[a-zA-Z0-9]/;
            if (regex.test(part)) {
              // Add the text part
              const textPart: AudioData = {
                id: generateAudioId({ id: `${id}-text`, text: part }),
                text: part,
              };
              sequence.parts.push(textPart);
            }
          }
        });

        return sequence;
      } catch (error) {
        console.error('Error processing phoneme sequence:', error);
        // Fallback to simple text if phoneme processing fails
        const processedText = text.replace(/{TARGET_TEXT_PHO}/g, '');
        const fallback: AudioData = {
          id: generateAudioId({ id, text: processedText }, index),
          text: processedText,
        };
        return fallback;
      }
    } else {
      const audioData: AudioData = {
        id: generateAudioId({ id, text: text }, index),
        text: text,
        containsPhonemes: false,
      };
      return audioData;
    }
  }

  /**
   * Flattens nested AudioDataSequence into a single array of AudioData
   * @param audioData The audio data or sequence to flatten
   * @returns Array of AudioData parts
   */
  private flattenAudioSequence(
    audioData: AudioData | AudioDataSequence
  ): AudioData[] {
    if ('type' in audioData && audioData.type === 'sequence') {
      // If it's a sequence, recursively flatten all its parts
      const flattenedParts: AudioData[] = [];
      for (let i = 0; i < audioData.parts.length; i++) {
        const part = audioData.parts[i];

        const subParts = this.flattenAudioSequence(part);
        flattenedParts.push(...subParts);
      }

      return flattenedParts;
    } else {
      // If it's a single AudioData, return it as an array
      return [audioData];
    }
  }

  /**
   * Processes a text field containing special tokens and replaces them with corresponding audio data.
   * The method handles both wordpart and phoneme tokens, creating either a simple AudioData object
   * or an AudioDataSequence depending on the presence of tokens.
   *
   * @param fieldValue - The input text containing potential ${<token>} patterns
   * @param id - A unique identifier used for generating audio IDs
   *
   * @returns {AudioData | AudioDataSequence} Either a simple AudioData object if no tokens are found,
   * or an AudioDataSequence containing both text and phoneme parts if tokens are present.
   * In case of errors during processing, returns a fallback AudioData object with the token removed.
   *
   * @throws Will log but handle any errors during token processing
   *
   * @example
   * // For text without tokens:
   * // Returns AudioData
   * service.replaceLearningTargetAndSuccessCriteriaTokens("simple text", "123");
   *
   * // For text with tokens:
   * // Returns AudioDataSequence
   * service.replaceLearningTargetAndSuccessCriteriaTokens("Read ${/k/}", "456");
   */
  replaceLearningTargetAndSuccessCriteriaTokens(
    fieldValue: string,
    id: string
  ): AudioData | AudioDataSequence {
    // replace all ${<some_token>} with the value of the token
    const { tokens } = this.parseWordpartAndPhonemeTokens(fieldValue);

    if (!tokens || !tokens[0]) {
      const audioData: AudioData = {
        id: generateAudioId({ id, text: fieldValue }),
        text: fieldValue,
        containsPhonemes: false,
      };
      return audioData;
    }

    const tokenAudioDataMap: { [key: string]: PhonemeProps[] } = {};

    tokens.forEach(token => {
      const { wordKey, parentKey } =
        this.parseIndividualWordPartOrPhonemeToken(token);
      if (parentKey === 'WORDPART') {
        tokenAudioDataMap[token] =
          this.amiraTTSService.getPhonemeSoundsForWordParts([wordKey.trim()]);
      } else if (parentKey === 'PHO') {
        tokenAudioDataMap[token] = this.amiraTTSService.getPhonemeSounds(
          wordKey.trim()
        );
      } else if (parentKey === 'SOUND') {
        // replace the token in fieldValue with the wordKey
        fieldValue = fieldValue.replace(token, wordKey);
      } else {
        tokenAudioDataMap[token] = [] as PhonemeProps[]; // If the token is not a word part or phoneme, initialize with empty array
      }
    });

    // If no phoneme audio data found, return simple AudioData instead of sequence
    if (!Object.keys(tokenAudioDataMap).length) {
      const audioData: AudioData = {
        id: generateAudioId({ id, text: fieldValue }),
        text: fieldValue,
        containsPhonemes: false,
      };
      return audioData;
    }

    try {
      // Split text by <anySpecialChars>${<some_token>}<anySpecialChars>
      const escapedDelimiter = tokens.map(
        token =>
          `([^a-zA-Z0-9_$]*)${token.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&'
          )}([^a-zA-Z0-9_$]*)`
      );
      const delimiterRegex = new RegExp(`(${escapedDelimiter.join('|')})`, 'g');
      const parts = fieldValue.split(delimiterRegex).filter(Boolean);

      const seqId = generateAudioId({ id, text: fieldValue });
      const sequence: AudioDataSequence = {
        id: seqId,
        parts: [],
        type: 'sequence',
        onStart: () => this.sequenceStarted$.next(seqId),
        onEnd: () => this.sequenceEnded$.next(seqId),
        onError: (error: Error) => {
          console.error('Error in audio sequence:', error);
          this.sequenceError$.next({ id: seqId, error });
        },
      };

      parts.forEach(part => {
        if (part.trim() === '') return; // Skip empty parts
        // Find any token that exists within this part
        const tokenInPart = tokens.find(token => part.includes(token));
        if (tokenInPart) part = tokenInPart; // Use the token if it exists in the part
        if (tokens.includes(part) && tokenAudioDataMap[part]) {
          sequence.parts.push(
            ...tokenAudioDataMap[part].map(props => ({
              id: `phoneme-${props.friendlyName}`,
              text: props.phoneme,
              url: props.url,
              containsPhonemes: true,
            }))
          );
        } else {
          const regex = /[a-zA-Z0-9]/;
          if (regex.test(part)) {
            // Add the text part
            const textPart: AudioData = {
              id: generateAudioId({ id: `${id}-text`, text: part }),
              text: part,
            };
            sequence.parts.push(textPart);
          }
        }
      });

      return sequence;
    } catch (error) {
      console.error('Error processing phoneme sequence:', error);
      // Fallback to simple text if phoneme processing fails
      const processedText = fieldValue.replace(tokens[0], '');
      const fallback: AudioData = {
        id: generateAudioId({ id, text: processedText }),
        text: processedText,
      };
      return fallback;
    }
  }

  /**
   * Replace {TARGET_TEXT} token with the current population's targetText
   * @param text The text to process
   * @returns The text with the token replaced
   */
  private replaceTargetTextToken(text: string): string {
    const targetText = this.populationToLoad?.targetText;

    const { tokens, parentKey, displayVal } =
      this.parseIndividualWordPartOrPhonemeToken(targetText ?? '');

    if (tokens && tokens[0]) {
      if (parentKey === 'WORDPART') {
        // If the token is a word part, replace it with the {TARGET_TEXT_PHO} token
        return text.replace(/{TARGET_TEXT}/g, '{TARGET_TEXT_PHO}');
      } else if (parentKey === 'PHO') {
        // If token is a PHO, replace targetText with the display value
        return text.replace(/{TARGET_TEXT}/g, displayVal ?? '');
      }
    }
    // If no tokens found, just replace with the population's targetText
    return text.replace(/{TARGET_TEXT}/g, targetText ?? '');
  }

  /**
   * Replace text tokens with appropriate audio content
   * @param text The text to process
   * @param id The base id for the audio
   * @param index Optional index for the audio
   * @returns Either AudioData for simple text or AudioSequence for text with phonemes
   */
  private async processTokens(
    text: string,
    id: string,
    index?: number
  ): Promise<AudioData | AudioDataSequence> {
    const targetTextReplaced = this.replaceTargetTextToken(text);
    return await this.replacePhonemeToken(targetTextReplaced, id, index);
  }

  /**
   * Process long text by splitting into sentences and flattening nested sequences
   * @param text The text to process
   * @param id The base id for the audio
   * @param index Optional index for the audio
   * @returns Either AudioData for short text or flattened AudioDataSequence for long text
   */
  async processLongText(
    text: string,
    id: string,
    index?: number
  ): Promise<AudioData | AudioDataSequence> {
    if (text.length <= MAX_TTS_CHARS) {
      // Use existing logic for shorter text
      return await this.processTokens(text, id, index);
    }

    // Split into sentence chunks
    const sentences = splitTextBySentences(
      text,
      this.configService.config.maxPerSentenceTTSChars ?? 150
    );

    // Create the main sequence for the chunked text
    const sequenceId = generateAudioId({ id, text: text }, index);
    const mainSequence: AudioDataSequence = {
      id: sequenceId,
      parts: [],
      type: 'sequence',
      onStart: () => {
        console.log(
          '▶️ [VerbalService.processLongText] Sequence started:',
          sequenceId
        );
        this.sequenceStarted$.next(id);
      },
      onEnd: () => {
        console.log(
          '⏹️ [VerbalService.processLongText] Sequence ended:',
          sequenceId
        );
        this.sequenceEnded$.next(id);
      },
      onError: (error: Error) => {
        console.error(
          '❌ [VerbalService.processLongText] Error in long text sequence:',
          {
            sequenceId,
            error: error.message,
            stack: error.stack,
          }
        );
        this.sequenceError$.next({ id, error });
      },
    };

    // Process each chunk and flatten any nested sequences
    for (let i = 0; i < sentences.length; i++) {
      const chunk = sentences[i];
      const chunkAudio = await this.processTokens(chunk, id, i);

      // Flatten the audio data to avoid nested sequences
      const flattenedParts = this.flattenAudioSequence(chunkAudio);

      mainSequence.parts.push(...flattenedParts);

      // Preserve phoneme flag if any chunk contains phonemes
      if ('containsPhonemes' in chunkAudio && chunkAudio.containsPhonemes) {
        mainSequence.containsPhonemes = true;
      }
    }

    return mainSequence;
  }

  /**
   * Load audio and store audio data for the current population.
   * @param callback optional callback to be called after audio loading attempt is finished
   * @returns `true` if all audio was loaded
   */
  // eslint-disable-next-line complexity
  async loadCurrentPopulation(
    callback?: (loaded: boolean) => void
  ): Promise<boolean> {
    if (!this.populationToLoad) {
      console.warn('No current population to load');
      return true;
    }

    // List and store audio data for current population based on audio type
    const audioDataBatch: (AudioData | AudioDataSequence)[] = [];
    const populationAudioData: PopulationAudioData = {};
    // set loading flag to true
    this._populationsAudioData[this.populationToLoad.id] = {
      status: AudioDataLoadStatus.LOADING,
      audioData: populationAudioData,
    };

    try {
      // prompt
      const questionText = this.populationToLoad.populationData.question?.text;
      await this.loadAudioData('question-text', questionText).then(
        audioData => {
          if (audioData) {
            populationAudioData.prompt = audioData;
            audioDataBatch.push(audioData);
          }
        }
      );

      // hints
      if (this.populationToLoad.hint) {
        populationAudioData.hints = [];
        for (const [index, hint] of this.populationToLoad.hint.entries()) {
          if (hint.data?.title) {
            const audioId = `hint-text-${index}`;
            this.loadAudioData(audioId, hint.data.title).then(audioData => {
              if (audioData) {
                populationAudioData.hints?.push(audioData);
                audioDataBatch.push(audioData);
              }
            });
          }
        }
      }

      if (
        this.populationToLoad.templateID === TemplateID.MULTIPLE_CHOICE ||
        this.populationToLoad.templateID === TemplateID.MULTIPLE_CHOICE_IMAGE
      ) {
        const populationData = this.populationToLoad
          .populationData as PopulationInlineChoice;
        // Load audio for all the choice targetedFeedback
        const answerChoices = populationData.answerChoices;
        if (answerChoices) {
          populationAudioData.answerChoices = {};
          for (const answerChoice of answerChoices) {
            if (answerChoice.targetedFeedback) {
              const firstFeedbacktext =
                answerChoice.targetedFeedback.firstIncorrect?.content;
              const secondFeedbackText =
                answerChoice.targetedFeedback.secondIncorrect?.content;
              if (firstFeedbacktext) {
                const audioId = `${answerChoice.id}-feedback-first-incorrect`;
                this.loadAudioData(audioId, firstFeedbacktext).then(
                  audioData => {
                    if (audioData && populationAudioData?.answerChoices) {
                      populationAudioData.answerChoices[audioId] = audioData;
                      audioDataBatch.push(audioData);
                    }
                  }
                );
              }
              if (secondFeedbackText) {
                const audioId = `${answerChoice.id}-feedback-second-incorrect`;
                this.loadAudioData(audioId, secondFeedbackText).then(
                  audioData => {
                    if (audioData && populationAudioData?.answerChoices) {
                      populationAudioData.answerChoices[audioId] = audioData;
                      audioDataBatch.push(audioData);
                    }
                  }
                );
              }
            }
          }
        }
      }

      // first incorrect
      await this.loadAudioData(
        'feedback-first-incorrect',
        this.populationToLoad.feedbackFirstIncorrect?.content
      ).then(audioData => {
        if (audioData) {
          populationAudioData.feedbackFirstIncorrect = audioData;
          audioDataBatch.push(audioData);
        }
      });

      // second incorrect
      await this.loadAudioData(
        'feedback-second-incorrect',
        this.populationToLoad.feedbackSecondIncorrect?.content
      ).then(audioData => {
        if (audioData) {
          populationAudioData.feedbackSecondIncorrect = audioData;
          audioDataBatch.push(audioData);
        }
      });

      // Load audio for list of audio data
      const currentPopId = this.populationToLoad.id;
      const loaded = await this.loadBatch(audioDataBatch);

      this._populationsAudioData[currentPopId] = {
        status: loaded ? AudioDataLoadStatus.LOADED : AudioDataLoadStatus.ERROR,
        audioData: populationAudioData,
      };

      // Call callback if provided
      callback?.(loaded);

      return loaded;
    } catch (error) {
      console.error('Error loading population audio:', error);
      callback?.(false);
      return false;
    }
  }

  async loadAudioData(
    id: string,
    text: string | undefined
  ): Promise<AudioData | AudioDataSequence | undefined> {
    if (text) {
      return await this.processLongText(text, id);
    }
    return undefined;
  }

  /**
   * Load audio for a batch of audio data or sequences
   * @param audioDataBatch batch of audio data or sequences
   * @returns `true` if all audio was loaded
   */
  async loadBatch(
    audioDataBatch: (AudioData | AudioDataSequence)[]
  ): Promise<boolean> {
    try {
      await Promise.all(
        audioDataBatch.map(async item => {
          if ('type' in item && item.type === 'sequence') {
            // For sequences, load each part
            await Promise.all(
              item.parts.map((part: AudioData) => {
                return part.containsPhonemes
                  ? this.loadPhoneme(part)
                  : this.load(part);
              })
            );
          } else {
            // For regular audio data, we know it's AudioData since it's not a sequence
            const audioData = item as AudioData;
            return audioData.containsPhonemes
              ? this.loadPhoneme(audioData)
              : this.load(audioData);
          }
        })
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Load audio for audio data using Amira text-to-speech.
   * @param audioData audio data
   */
  async load(audioData: AudioData): Promise<void> {
    if (!audioData.id) {
      return Promise.reject(
        new Error(`No ID provided in audio data ${audioData}`)
      );
    }

    if (!audioData.text) {
      return Promise.reject(
        new Error(`No text provided in audio data ${audioData}`)
      );
    }

    const sanitizedText = replaceSpecialCharacters(
      audioData.text,
      this.document
    );

    const utterance = await this.amiraTTSService
      .getUtterance(sanitizedText, false, 'en-us', 16000, true)
      .catch(error => {
        return Promise.reject(
          new Error(
            `Failed to load audio for text: ${sanitizedText} - ${error}`,
            error
          )
        );
      });

    const downloadedAudioData = {
      ...audioData,
      url: utterance.audioURL,
    };

    if (!this.audioService.isSoundLoaded(downloadedAudioData.id)) {
      await this.audioService.initSound(
        downloadedAudioData,
        (id: string) => this.onSpeechStarted(id),
        (id: string) => this.onSpeechEnded(id),
        (id: string, error: unknown) => this.onSpeechError(id, error)
      );
    }

    Promise.resolve();
  }

  onSpeechStarted = (id: string): void => {
    this.speechStarted$.next(id);
  };

  onSpeechEnded = (id: string): void => {
    this.speechEnded$.next(id);
  };

  onSpeechError = (id: string, error: unknown): void => {
    if (!id) {
      return;
    }

    this.speechError$.next({ id, error });
  };

  async loadPhoneme(audioData: AudioData): Promise<void> {
    if (!audioData.id) {
      throw new Error('No ID provided in audio data');
    }

    if (!audioData.text) {
      throw new Error('No text provided in audio data');
    }

    if (!audioData.url) {
      throw new Error('No URL provided in audio data');
    }

    const downloadedAudioData = {
      ...audioData,
      containsPhonemes: true,
    };

    if (!this.audioService.isSoundLoaded(downloadedAudioData.id)) {
      await this.audioService.initSound(
        downloadedAudioData,
        (id: string) =>
          this.onPhonemeStarted(
            id,
            downloadedAudioData.triggerCorrectiveFeedback
          ),
        (id: string) =>
          this.onPhonemeEnded(
            id,
            downloadedAudioData.triggerCorrectiveFeedback
          ),
        (id: string, error: unknown) =>
          this.onPhonemeError(
            id,
            error,
            downloadedAudioData.triggerCorrectiveFeedback
          )
      );
    }
  }

  onPhonemeStarted = (
    id: string,
    shouldTriggerCorrectiveFeedback = false
  ): void => {
    this.phonemeStarted$.next(`${id}|${shouldTriggerCorrectiveFeedback}`);
  };

  onPhonemeEnded = (
    id: string,
    shouldTriggerCorrectiveFeedback = false
  ): void => {
    this.phonemeEnded$.next(`${id}|${shouldTriggerCorrectiveFeedback}`);
  };

  onPhonemeError = (
    id: string,
    error: unknown,
    shouldTriggerCorrectiveFeedback = false
  ): void => {
    if (!id) {
      return;
    }

    this.phonemeError$.next({
      id: `${id}|${shouldTriggerCorrectiveFeedback}`,
      error,
    });
  };

  // #region Playing

  /**
   * Play the prompt audio for the current population.
   * @param type type of audio to play
   * @param delay optional flag to delay playing prompt by `PROMPT_TRANSITION_DELAY_MS`
   */
  // eslint-disable-next-line complexity
  async playPopulationAudio(
    type: VerbalAudioType | string,
    delay = false
  ): Promise<void> {
    this.clearPromptPlayDelayTimer();
    let audioData: AudioData | undefined = undefined;
    switch (type) {
      case VerbalAudioType.PROMPT:
        audioData = this.currentPopulationAudioData?.prompt;
        break;
      case VerbalAudioType.HINT:
        if (this.currentPopulationAudioData?.hints?.length) {
          audioData =
            this.currentPopulationAudioData?.hints[this.currentHintIndex];
          // Move to next hint, wrapping around to start if needed
          this.currentHintIndex =
            (this.currentHintIndex + 1) %
            this.currentPopulationAudioData?.hints?.length;
        }
        break;
      case VerbalAudioType.FEEDBACK_FIRST_INCORRECT:
        audioData = this.currentPopulationAudioData?.feedbackFirstIncorrect;
        break;
      case VerbalAudioType.FEEDBACK_SECOND_INCORRECT:
        audioData = this.currentPopulationAudioData?.feedbackSecondIncorrect;
        break;
      default: {
        const answerChoices = this.currentPopulationAudioData?.answerChoices;
        if (answerChoices && type in answerChoices) {
          audioData = answerChoices[type];
        } else {
          console.warn(`Invalid audio type: ${type}`);
          return;
        }
      }
    }

    if (!audioData) {
      console.warn(`No audio data found for type: ${type}`);
    }

    if (delay) {
      this.promptPlayDelayTimeoutId = setTimeout(() => {
        this.promptPlayDelayTimeoutId = null;
        void (async (): Promise<void> => {
          if (audioData) {
            await this.play(audioData);
          }
        })();
      }, PROMPT_TRANSITION_DELAY_MS);
    } else {
      audioData && (await this.play(audioData));
    }
  }

  /**
   * Play a sequence of audio parts
   * @param sequence The audio sequence to play
   */
  async playSequence(sequence: AudioDataSequence): Promise<void> {
    sequence.onStart?.();

    try {
      // Play each part in sequence
      for (const part of sequence.parts) {
        // Stream that emits `true` when THIS part finishes
        if (this.currentSequenceId !== sequence.id) {
          break;
        }
        const end$ = (
          part.containsPhonemes ? this.phonemeEnded$ : this.speechEnded$
        ).pipe(
          filter(id => {
            return part.containsPhonemes
              ? parseAndGetLastValueAfterToken(id, '|').remStr === part.id
              : id === part.id;
          }),
          map(() => true),
          take(1)
        );

        // Stream that throws when THIS part fails
        const err$ = (
          part.containsPhonemes ? this.phonemeError$ : this.speechError$
        ).pipe(
          filter(({ id }) => {
            return part.containsPhonemes
              ? parseAndGetLastValueAfterToken(id, '|').remStr === part.id
              : id === part.id;
          }),
          map(({ error }) => {
            throw new Error(`Error playing part ${part.id}`, { cause: error });
          }),
          take(1)
        );

        // Set current ID and play the sound
        this.currentId = part.id;
        await this.audioService.playSound(part, false, false, true); //To play the sound even if there is a SFX playing already

        // Wait for whichever happens first
        await firstValueFrom(merge(end$, err$));

        // Add a small pause between parts for natural spacing
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Clear the BehaviourSubjects after the sequence ends so that old value does not linger
      this.phonemeEnded$.next(''); // Clear the stream
      this.speechEnded$.next(''); // Clear the stream
      this.phonemeError$.next({ id: '', error: new Error('') }); // Clear the stream
      this.speechError$.next({ id: '', error: new Error('') }); // Clear the stream
      sequence.onEnd?.();
    } catch (error) {
      console.error('Verbal Service Error playing sequence:', error);
      sequence.onError?.(error as Error);
      sequence.onEnd?.();
    }
  }

  /**
   * Play audio for audio data or sequence
   * @param audioData audio data or sequence
   */
  async play(audioData: AudioData | AudioDataSequence): Promise<void> {
    this.stopCurrent();

    if ('type' in audioData && audioData.type === 'sequence') {
      this.currentId = audioData.id;
      this.currentSequenceId = audioData.id;
      await this.playSequence(audioData);
    } else {
      this.currentId = audioData.id;
      this.currentSequenceId = '';
      this.audioService.playSound(audioData, false, false, true); //To play the sound even if there is a SFX playing already
    }
  }

  /**
   * Stop the currently playing audio if necessary.
   */
  stopCurrent(): void {
    this.clearPromptPlayDelayTimer();
    this.audioService.stopSound(this.currentId);
    if (this.currentSequenceId) this.currentSequenceId = '';
    // Clear stale pause/resume state so that new TEI subscribers (e.g. on the
    // next activity's first question) don't receive a replayed "resumed" event
    // from the previous lesson and accidentally re-disable the toolbar.
    this.audioPaused$.next('');
    this.audioResumed$.next('');
  }

  private clearPromptPlayDelayTimer(): void {
    if (this.promptPlayDelayTimeoutId !== null) {
      clearTimeout(this.promptPlayDelayTimeoutId);
      this.promptPlayDelayTimeoutId = null;
    }
  }

  /**
   * Toggle pause/resume for the currently playing audio or sequence.
   * For sequences, this stops/starts the sequence. For individual audio, it pauses/resumes.
   */
  togglePause(): void {
    if (this.currentId) {
      const isPaused = this.audioService.toggleSoundPause(this.currentId);

      // Emit appropriate event based on the new state
      if (isPaused) {
        this.audioPaused$.next(this.currentId);
      } else {
        this.audioResumed$.next(this.currentId);
      }
    }
  }

  /**
   * Parses a string containing special token syntax (${...}) to extract tokens and create a display string
   * @param fieldValue - The input string containing tokens in the format ${...}
   * @returns An object containing:
   *  - tokens: Array of extracted tokens if found, undefined otherwise
   *  - displayStr: The processed string with tokens replaced by their display values
   * @example
   * // Input: "The ${wordPart | highlighted} is highlighted"
   * // Output: {
   * //   tokens: ["${wordPart}"],
   * //   displayStr: "The highlighted is highlighted"
   * // }
   */
  parseWordpartAndPhonemeTokens(fieldValue: string): {
    tokens: string[] | undefined;
    displayStr: string;
    hasPhoOrWordpartToken?: boolean;
  } {
    // eslint-disable-next-line no-useless-escape
    const tokens = fieldValue.match(/\$\{([^\}]+)\}/g);
    if (!tokens || !tokens[0]) {
      return {
        tokens: undefined,
        displayStr: fieldValue,
      };
    }

    let displayStr = fieldValue;
    let hasPhoOrWordpartToken = false;
    // replace all the tokens in the fieldValue with the display value
    tokens.forEach(token => {
      const {
        displayVal,
        tokens: tokenArr,
        parentKey,
      } = this.parseIndividualWordPartOrPhonemeToken(token);
      if (tokenArr && tokenArr[0]) {
        displayStr = displayStr.replace(tokenArr[0], displayVal);
      }
      if (parentKey === 'PHO' || parentKey === 'WORDPART') {
        // If the token is a phoneme or word part, set hasOnlyPhoOrWordPartTokens to true
        hasPhoOrWordpartToken = true;
      }
    });

    return {
      tokens,
      displayStr,
      hasPhoOrWordpartToken,
    };
  }

  /**
   * Parses a string containing special tokens in the format ${parent_word|display}
   * and extracts the token components.
   *
   * @param fieldValue - The input string containing tokens to parse
   * @returns An object containing:
   *   - tokens: Array of matched token strings or null if no matches
   *   - displayVal: The display value portion of the token
   *   - actualToken: The token without the ${} wrapper
   *   - parentKey: The parent portion of the token ('WORDPART' | 'PHO')
   *   - wordKey: The word portion of the token
   *   - displayStr: The input string with tokens replaced by display values
   */
  parseIndividualWordPartOrPhonemeToken(
    token: string
  ): WordPartAndPhonemeTokenKeys {
    // eslint-disable-next-line no-useless-escape
    const tokens = token.match(/\$\{([^\}]+)\}/g);
    if (!tokens || !tokens[0]) {
      return {
        tokens: null,
        displayVal: '',
        actualToken: '',
        parentKey: '',
        wordKey: '',
        displayStr: token,
      };
    }
    const actualToken = tokens[0].replace('${', '').replace('}', '');
    const [metadata, displayVal] = actualToken.split('|');
    const [parentKey, wordKey] = metadata.split('_');
    const displayStr = token.replace(tokens[0], displayVal);
    return {
      tokens,
      displayVal: displayVal?.trim() || '',
      actualToken,
      parentKey,
      wordKey: wordKey?.trim().toLowerCase() || '',
      displayStr,
    };
  }
}
